import React from 'react'
import GeneralInformationForm from './GeneralInformationForm'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import effects from 'app/effects/effects'
import { generalMappingToApi } from '../mapping/profileMapping'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { isEmpty, isNil } from 'ramda'
import {
  useFormContext,
  TEXT_50_MAX,
  TEXT_200_MAX,
  PHONETIC_TEXT_MAX,
} from '../FormProvider'

const regex = /^\s*\p{L}/u

const validationSchema = (formatMessage) => {
  return Yup.object({
    firstName: Yup.string()
      .required(formatMessage({ id: 'composeDialog.field.required' }))
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    lastName: Yup.string()
      .required(formatMessage({ id: 'composeDialog.field.required' }))
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    middleName: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    phoneticLastName: Yup.string()
      .nullable()
      .max(
        PHONETIC_TEXT_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: PHONETIC_TEXT_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    phoneticMiddleName: Yup.string()
      .nullable()
      .max(
        PHONETIC_TEXT_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: PHONETIC_TEXT_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    phoneticFirstName: Yup.string()
      .nullable()
      .max(
        PHONETIC_TEXT_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: PHONETIC_TEXT_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    prefix: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    suffix: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    formalTitle: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    nickname: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    dateOfBirth: Yup.string().test(
      'dateOfBirth-valid-date',
      formatMessage({ id: 'importCsv.validation.date.futureDate' }),
      (value) => {
        if (isEmpty(value) || isNil(value)) {
          return true
        }
        const date = moment(value)
        if (!date.isValid()) {
          return false
        } else {
          return date.isBefore(moment())
        }
      },
    ),
    placeOfBirth: Yup.string()
      .nullable()
      .max(
        TEXT_200_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_200_MAX },
        ),
      )
      .matches(regex, formatMessage({ id: 'validation.first.character' })),
    passportId: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      ),
    uniqueId: Yup.string()
      .nullable()
      .max(
        TEXT_50_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_50_MAX },
        ),
      ),
    gender: Yup.string().nullable(),
    passportExpiresAt: Yup.string().test(
      'passportExpiresAt-valid-date',
      formatMessage({ id: 'subscriberDetail.passport.expiration' }),
      (value) => {
        if (isEmpty(value) || isNil(value)) {
          return true
        }
        const date = moment(value)
        if (!date.isValid()) {
          return false
        } else {
          return date.isBefore(moment().add(12, 'years'))
        }
      },
    ),
  })
}

export default ({ initailValue, subscriberId, countries, refetchData }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const {
    onGeneralChange,
    onGeneralReset,
    isShowTranslate,
    isShowDetect,
    checkTranslate,
    checkTranslateFirstState,
    onTranslate,
    closeWarningTranslate,
    closeDetact,
  } = useFormContext()

  const updateGeneralInfo = (
    values,
    { resetForm, setSubmitting, setFieldError },
  ) => {
    const info = { type: 'general', payload: generalMappingToApi(values) }
    effects.apis
      .updateProfileDetailOfSubscriber({ subscriberId, info })
      .then((response) => {
        setSubmitting(false)
        onGeneralReset()
        resetForm({ values })
        refetchData(subscriberId)
        dispatch(
          MessageAlert.showSuccess({
            message: formatMessage({ id: 'message.alert.success' }),
          }),
        )
      })
      .catch((error) => {
        setSubmitting(false)
        const errorCode = error?.response?.data?.details?.code
        if (errorCode === 403) {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'error.permission.denied' }),
            }),
          )
        } else if (errorCode === 618) {
          setFieldError(
            'uniqueId',
            formatMessage({ id: 'uniqueId.already.exist' }),
          )
        } else {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'message.alert.error' }),
            }),
          )
        }
      })
  }
  return (
    <div className="w-full">
      <Formik
        initialValues={initailValue}
        validationSchema={validationSchema(formatMessage)}
        onSubmit={updateGeneralInfo}
      >
        <Form>
          <GeneralInformationForm
            subscriberId={subscriberId}
            countries={countries}
            onGeneralChange={onGeneralChange}
            onGeneralReset={onGeneralReset}
            isShowTranslate={isShowTranslate}
            isShowDetect={isShowDetect}
            checkTranslate={checkTranslate}
            checkTranslateFirstState={checkTranslateFirstState}
            onTranslate={onTranslate}
            closeWarningTranslate={closeWarningTranslate}
            closeDetact={closeDetact}
          />
        </Form>
      </Formik>
    </div>
  )
}
